import {
  useState,
  type ComponentType,
  type ReactNode,
  type SVGProps,
} from "react";
import { Dialog } from "@headlessui/react";
import {
  CheckIcon,
  CubeIcon,
  MapPinIcon,
  PencilIcon,
  Square2StackIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { FACILITY } from "../../constants";
import TrackingEditForm from "../../pages/status/components/trackingEditForm";
import { useUpdateShippingStatus } from "../../hooks/useUpdateShippingStatus";
import { routeEndpoints } from "../../routes/routeEndpoints";
import type { OrderFormValues } from "../../types/forms";

const facilityAddressText = `${FACILITY.name}, ${FACILITY.address}`;

interface OrderSubmittedModalProps {
  isOpen: boolean;
  orderId: string;
  trackingInfo?: OrderFormValues["trackingInfo"];
  onClose: () => void;
}

const OrderSubmittedModal = ({
  isOpen,
  orderId,
  trackingInfo,
  onClose,
}: OrderSubmittedModalProps) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isAddressCopied, setIsAddressCopied] = useState(false);
  const updateMutation = useUpdateShippingStatus(orderId, {
    onSuccess: () => setIsEditing(false),
  });

  if (!isOpen) return null;

  const copyAddress = async () => {
    await navigator.clipboard.writeText(facilityAddressText);
    setIsAddressCopied(true);
    setTimeout(() => setIsAddressCopied(false), 2000);
  };

  const CopyIcon = isAddressCopied ? CheckIcon : Square2StackIcon;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div
        className="fixed inset-0 bg-black/45 backdrop-blur-sm"
        aria-hidden="true"
      />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-150 max-h-full overflow-y-auto rounded-2xl bg-white md:p-8 p-5 shadow-xl">
          <div className="flex flex-col items-center">
            <div className="flex size-14 items-center justify-center rounded-full bg-green-50">
              <CheckIcon className="size-7 text-green-500" />
            </div>
            <Dialog.Title className="mt-4 md:text-2xl text-xl font-semibold text-gray-950">
              Order Submitted!
            </Dialog.Title>
          </div>

          <div className="mt-8 flex flex-col gap-6">
            <InfoRow
              Icon={MapPinIcon}
              label="Your item has been sent to our facility at:"
            >
              <div className="flex items-center gap-3">
                <p className="flex-1 text-sm md:text-base text-gray-950">
                  {facilityAddressText}
                </p>
                <button
                  type="button"
                  onClick={copyAddress}
                  aria-label={isAddressCopied ? "Copied" : "Copy address"}
                  className="shrink-0 p-1 rounded-md text-gray-500 hover:bg-gray-100 cursor-pointer transition-colors"
                >
                  <CopyIcon className="size-5" />
                </button>
              </div>
            </InfoRow>

            {trackingInfo?.trackingNumber && (
              <InfoRow Icon={CubeIcon} label="Shipment awaiting">
                {isEditing ? (
                  <TrackingEditForm
                    defaultTrackingNumber={trackingInfo.trackingNumber}
                    defaultCarrier={trackingInfo.carrier}
                    isSaving={updateMutation.isPending}
                    onCancel={() => setIsEditing(false)}
                    onSave={(updatedTrackingInfo) =>
                      updateMutation.mutate({
                        trackingInfo: updatedTrackingInfo,
                        trackingImage: null,
                      })
                    }
                  />
                ) : (
                  <div className="flex items-center gap-3">
                    <p className="flex-1 text-sm md:text-base text-gray-950 break-all">
                      {trackingInfo.trackingNumber}
                    </p>
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      aria-label="Edit tracking ID"
                      className="shrink-0 p-1 rounded-md text-gray-500 hover:bg-gray-100 cursor-pointer transition-colors"
                    >
                      <PencilIcon className="size-5" />
                    </button>
                  </div>
                )}
              </InfoRow>
            )}
          </div>

          <div className="mt-8 flex flex-col md:gap-5 gap-3">
            <button
              type="button"
              onClick={() =>
                navigate(`${routeEndpoints.orderDetails}?id=${orderId}`)
              }
              className="h-13 w-full rounded-xl text-base font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
            >
              View order
            </button>
            <button
              type="button"
              onClick={() => navigate(routeEndpoints.orders)}
              className="h-13 w-full rounded-xl text-base font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

const InfoRow = ({
  Icon,
  label,
  children,
}: {
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  children: ReactNode;
}) => (
  <div className="flex items-start gap-4">
    <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-blue-50">
      <Icon className="size-5 text-blue-600" />
    </div>
    <div className="flex flex-1 flex-col gap-1 min-w-0">
      <p className="text-sm text-gray-500">{label}</p>
      {children}
    </div>
  </div>
);

export default OrderSubmittedModal;
